<template>
	<div class="avatar__wrapper">
		<helio-icon
			v-if="badge"
			:class="[
				`avatar__badge avatar__badge--${size} avatar__badge--${badgePosition}`,
				{
					'avatar__badge--clickable': badge.clickable ? badge.clickable : false,
				}
			]"
			:token="badge.token ? badge.token : 'shield-alt'"
			:color="badge.color ? badge.color : 'white'"
			:weight="badge.weight ? badge.weight : 'bold'"
			@click="badgeClicked"
		/>

		<helio-tooltip
			v-if="tooltip"
			:active="showTooltip"
			:anchor="`tooltip_${_uid}`"
			:gap="15"
		>
			<template v-slot:content>
				{{ tooltip }}
			</template>
		</helio-tooltip>

		<div
			:class="classes"
			:style="hasImage ? `background-image: url('${hasImage}')` : ''"
			:data-anchor="tooltip ? `tooltip_${_uid}` : ''"
			data-cy="avatar"
			@click="$emit('click')"
			@mouseover="showTooltip = true"
			@mouseleave="showTooltip = false"
		>
			<span
				v-if="showInitials"
				:class="`avatar__initials avatar__initials--${size}`"
			>
				{{ initials }}
			</span>

			<helio-icon
				v-if="showDefault"
				:custom-classes="`avatar__icon avatar__icon--${size}`"
				:color="iconColor"
				weight="bold"
				token="user"
			/>
		</div>
	</div>
</template>

<script>
import Icon from '../icon/Icon.vue';
import Tooltip from '../tooltip/Tooltip.vue';

export default {
	name: 'Avatar',

	components: {
		'helio-icon': Icon,
		'helio-tooltip': Tooltip,
	},

	props: {
		animation: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the avatar will have a hover animation',
		},

		backgroundColor: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the background-color of the Avatar',
		},

		badge: {
			type: [Boolean, Object],
			required: false,
			default: null,
			description: 'Indicates whether or not the Avatar will have a badge. An object may be passed to style the badge\'s token (i.e., icon), position, colour and weight—all of these object entries are optional.',
		},

		iconColor: {
			type: String,
			required: false,
			default: 'sky-gray6',
			description: 'Sets the color of the Avatar icon in default display',
		},

		initialsColor: {
			type: String,
			required: false,
			default: 'accent1-normal',
			description: 'Sets the background-color of the Avatar when it displays the user\'s initials',
		},

		size: {
			type: String,
			required: false,
			default: 'medium',
			validator: value => ['xx-small', 'x-small', 'small', 'medium', 'large', 'x-large', 'xx-large'].indexOf(value) !== -1,
			description: 'Sets the size of the Avatar',
		},

		tooltip: {
			type: String,
			required: false,
			default: null,
			description: 'Adds a tooltip containing the provided string&mdash;it will appear when the user hovers on the Avatar',
		},

		user: {
			type: Object,
			required: false,
			default: () => ({}),
			description: 'Supplies information about a user to the Avatar component. The object passed can provide the user image and name, though these object entries are both optional.',
		},
	},

	data() {
		return {
			showTooltip: false,
		};
	},

	computed: {
		avatarColor() {
			if (this.backgroundColor) {
				return this.backgroundColor;
			}

			if (this.showDefault) {
				return 'sky-gray7';
			}

			if (this.user.image) {
				// We show a night gray background
				// while the user image is loading
				return 'night-gray1';
			}

			return this.initialsColor;
		},

		classes() {
			const defaultClasses = `avatar avatar--${this.size} avatar--${this.avatarColor} ${this.animation ? 'avatar--animation' : ''}`;

			if (this.user.image) {
				return `${defaultClasses} avatar--image`;
			}

			return defaultClasses;
		},

		hasImage() {
			return this.user.image;
		},

		showDefault() {
			return !this.user.name && !this.user.image;
		},

		showInitials() {
			return this.user.name && !this.user.image;
		},

		initials() {
			const { name } = this.user;
			const parts = name.split(' ');

			if (name) {
				let initials = '';

				for (let i = 0; i < parts.length; i += 1) {
					if (parts[i].length > 0 && parts[i] !== '') {
						initials += parts[i][0];
					}
				}

				return initials;
			}

			return false;
		},

		badgePosition() {
			return this.badge.position ? this.badge.position : 'bottom-left';
		},
	},

	methods: {
		badgeClicked() {
			if (this.badge && this.badge.clickable) {
				this.$emit('badgeClick');
			}
		},
	},
};
</script>

<style lang="scss" scoped src="./avatar.scss" />
